import { useCallback, useState } from 'react';
import { isTauri } from '@tauri-apps/api/core';
import { Box, Button, config, Menu, Spinner, Text } from 'folds';
import type { AuthDict, IAuthData, IMyDevice, MatrixError, UIAFlow } from '$types/matrix-sdk';
import { SequenceCard, SequenceCardStyle } from '$components/sequence-card';
import { ActionUIA, ActionUIAFlowsLoader } from '$components/ActionUIA';
import type { AsyncState } from '$hooks/useAsyncCallback';
import { AsyncStatus, useAsync } from '$hooks/useAsyncCallback';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useUIAMatrixError } from '$hooks/useUIAFlows';
import { DeviceVerificationStatus } from '$components/DeviceVerificationStatus';
import { VerificationStatus } from '$hooks/useDeviceVerificationStatus';
import { useAuthMetadata } from '$hooks/useAuthMetadata';
import { getAccountManagementUrl, useAccountManagementActions } from '$hooks/useAccountManagement';
import { SettingTile } from '$components/setting-tile';
import { VerifyOtherDeviceTile } from './Verification';
import { DeviceDeleteBtn, DeviceTile } from './DeviceTile';

function renderUnsupportedUIAFlow() {
  return (
    <Text size="T200">
      Authentication steps to perform this action are not supported by client.
    </Text>
  );
}

type DeleteDevicesUIAProps = {
  authData: IAuthData;
  ongoingFlow: UIAFlow;
  deleteDevices: (authDict?: AuthDict) => void;
  onCancel: () => void;
};

function DeleteDevicesUIA({
  authData,
  ongoingFlow,
  deleteDevices,
  onCancel,
}: DeleteDevicesUIAProps) {
  return (
    <ActionUIA
      authData={authData}
      ongoingFlow={ongoingFlow}
      action={deleteDevices}
      onCancel={onCancel}
    />
  );
}

type OtherDevicesProps = {
  devices: IMyDevice[];
  refreshDeviceList: () => Promise<void>;
  showVerification?: boolean;
};
export function OtherDevices({ devices, refreshDeviceList, showVerification }: OtherDevicesProps) {
  const mx = useMatrixClient();
  const crypto = mx.getCrypto();
  const authMetadata = useAuthMetadata();
  const accountManagementActions = useAccountManagementActions();

  const [deleted, setDeleted] = useState(new Set());

  const handleDashboardOIDC = useCallback(() => {
    const url = getAccountManagementUrl(
      authMetadata,
      accountManagementActions.sessionsList,
      undefined,
      mx.getHomeserverUrl()
    );
    if (!url) return;
    if (isTauri()) {
      import('@tauri-apps/plugin-opener')
        .then(({ openUrl }) => openUrl(url))
        .catch(() => window.open(url, '_blank'));
      return;
    }
    window.open(url, '_blank');
  }, [authMetadata, accountManagementActions, mx]);

  const handleDeleteOIDC = useCallback(
    (deviceId: string) => {
      const url = getAccountManagementUrl(
        authMetadata,
        accountManagementActions.sessionEnd,
        deviceId,
        mx.getHomeserverUrl()
      );
      if (!url) return;
      if (isTauri()) {
        import('@tauri-apps/plugin-opener')
          .then(({ openUrl }) => openUrl(url))
          .catch(() => window.open(url, '_blank'));
        return;
      }
      window.open(url, '_blank');
    },
    [authMetadata, accountManagementActions, mx]
  );

  const handleToggleDelete = useCallback((deviceId: string) => {
    setDeleted((deviceIds) => {
      const newIds = new Set(deviceIds);
      if (newIds.has(deviceId)) {
        newIds.delete(deviceId);
      } else {
        newIds.add(deviceId);
      }
      return newIds;
    });
  }, []);

  const [deleteState, setDeleteState] = useState<AsyncState<void, MatrixError>>({
    status: AsyncStatus.Idle,
  });

  const deleteDevices = useAsync(
    useCallback(
      async (authDict?: AuthDict) => {
        await mx.deleteMultipleDevices(Array.from(deleted) as string[], authDict);
      },
      [mx, deleted]
    ),
    useCallback(
      (state: typeof deleteState) => {
        if (state.status === AsyncStatus.Success) {
          setDeleted(new Set());
          refreshDeviceList();
        }
        setDeleteState(state);
      },
      [refreshDeviceList]
    )
  );
  const [authData, deleteError] = useUIAMatrixError(
    deleteState.status === AsyncStatus.Error ? deleteState.error : undefined
  );
  const deleting = deleteState.status === AsyncStatus.Loading || authData !== undefined;

  const handleCancelDelete = () => setDeleted(new Set());
  const handleCancelAuth = useCallback(() => {
    setDeleteState({ status: AsyncStatus.Idle });
  }, []);

  const renderDeleteDevicesUIA = useCallback(
    (ongoingFlow: UIAFlow) =>
      authData ? (
        <DeleteDevicesUIA
          authData={authData}
          ongoingFlow={ongoingFlow}
          deleteDevices={deleteDevices}
          onCancel={handleCancelAuth}
        />
      ) : null,
    [authData, deleteDevices, handleCancelAuth]
  );

  return devices.length > 0 ? (
    <>
      <Box direction="Column" gap="100">
        <Text size="L400">Others</Text>
        {authMetadata?.account_management_uri && (
          <SequenceCard
            className={SequenceCardStyle}
            variant="SurfaceVariant"
            direction="Column"
            gap="400"
          >
            <SettingTile
              title="Device Dashboard"
              focusId="device-dashboard"
              description="Manage your devices on OIDC dashboard."
              after={
                <Button
                  size="300"
                  variant="Secondary"
                  fill="Soft"
                  radii="300"
                  outlined
                  onClick={handleDashboardOIDC}
                >
                  <Text size="B300">Open</Text>
                </Button>
              }
            />
          </SequenceCard>
        )}
        {devices
          .toSorted((d1, d2) => {
            return (d1.last_seen_ts ?? 0) < (d2.last_seen_ts ?? 0) ? 1 : -1;
          })
          .map((device) => (
            <SequenceCard
              key={device.device_id}
              className={SequenceCardStyle}
              variant={deleted.has(device.device_id) ? 'Critical' : 'SurfaceVariant'}
              direction="Column"
              gap="400"
            >
              <DeviceTile
                device={device}
                deleted={deleted.has(device.device_id)}
                refreshDeviceList={refreshDeviceList}
                disabled={deleting}
                options={
                  authMetadata?.account_management_uri ? (
                    <DeviceDeleteBtn
                      deviceId={device.device_id}
                      deleted={false}
                      onDeleteToggle={handleDeleteOIDC}
                    />
                  ) : authMetadata ? undefined : (
                    <DeviceDeleteBtn
                      deviceId={device.device_id}
                      deleted={deleted.has(device.device_id)}
                      onDeleteToggle={handleToggleDelete}
                      disabled={deleting}
                    />
                  )
                }
              />
              {showVerification && crypto && (
                <DeviceVerificationStatus
                  crypto={crypto}
                  userId={mx.getSafeUserId()}
                  deviceId={device.device_id}
                >
                  {(status) =>
                    status === VerificationStatus.Unverified && (
                      <VerifyOtherDeviceTile crypto={crypto} deviceId={device.device_id} />
                    )
                  }
                </DeviceVerificationStatus>
              )}
            </SequenceCard>
          ))}
      </Box>
      {deleted.size > 0 && (
        <Menu
          style={{
            position: 'sticky',
            padding: config.space.S200,
            paddingLeft: config.space.S400,
            bottom: config.space.S400,
            left: config.space.S400,
            right: 0,
            zIndex: 1,
          }}
          variant="Critical"
        >
          <Box alignItems="Center" gap="400">
            <Box grow="Yes" direction="Column">
              {deleteError ? (
                <Text size="T200">
                  <b>Failed to logout devices! Please try again. {deleteError.message}</b>
                </Text>
              ) : (
                <Text size="T200">
                  <b>Logout from selected devices. ({deleted.size} selected)</b>
                </Text>
              )}
              {authData && (
                <ActionUIAFlowsLoader authData={authData} unsupported={renderUnsupportedUIAFlow}>
                  {renderDeleteDevicesUIA}
                </ActionUIAFlowsLoader>
              )}
            </Box>
            <Box shrink="No" gap="200">
              <Button
                size="300"
                variant="Critical"
                fill="None"
                radii="300"
                disabled={deleting}
                onClick={handleCancelDelete}
              >
                <Text size="B300">Cancel</Text>
              </Button>
              <Button
                size="300"
                variant="Critical"
                radii="300"
                disabled={deleting}
                before={deleting && <Spinner variant="Critical" fill="Solid" size="100" />}
                onClick={() => deleteDevices()}
              >
                <Text size="B300">Logout</Text>
              </Button>
            </Box>
          </Box>
        </Menu>
      )}
    </>
  ) : null;
}
